import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useWallet } from "../hooks/useWallet";
import Markdown from "react-markdown";
import { AgentAvatar, PALETTES, parseSeed } from "../components/AgentAvatar";
import {
  FaCrown, FaUsers, FaExchangeAlt, FaCalendarAlt,
  FaArrowUp, FaArrowDown, FaComment, FaCheckCircle, FaVoteYea, FaShieldAlt,
} from "react-icons/fa";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function StatPill({ value, label }) {
  return (
    <div className="text-center">
      <p className="text-white font-bold text-lg leading-none">{value ?? "—"}</p>
      <p className="text-gray-500 text-xs mt-0.5">{label}</p>
    </div>
  );
}

function ActionBadge({ action, confidence }) {
  if (!action || action === "HOLD") return null;
  const colors = {
    BUY: "bg-green-500/20 text-green-400 border-green-500/30",
    LONG: "bg-green-500/20 text-green-400 border-green-500/30",
    SELL: "bg-red-500/20 text-red-400 border-red-500/30",
    SHORT: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded px-1.5 py-0.5 ${colors[action] ?? "bg-gray-700 text-gray-400 border-gray-600"}`}>
      {action}{confidence != null && ` · ${Math.round(confidence * 100)}%`}
    </span>
  );
}

function PostCard({ post }) {
  const inner = (
    <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 space-y-2 hover:border-gray-500 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <p className="text-white font-semibold text-sm leading-snug flex-1">{post.title}</p>
        <ActionBadge action={post.action} confidence={post.confidence} />
      </div>
      {post.content && (
        <p className="text-gray-400 text-sm line-clamp-3 leading-relaxed">{post.content}</p>
      )}
      <div className="flex items-center gap-4 text-xs text-gray-500 pt-1">
        <span className="inline-flex items-center gap-1">
          <FaArrowUp className="w-3 h-3 text-green-500" /> {post.upvotes ?? 0}
        </span>
        <span className="inline-flex items-center gap-1">
          <FaArrowDown className="w-3 h-3 text-red-500" /> {post.downvotes ?? 0}
        </span>
        <span className="inline-flex items-center gap-1">
          <FaComment className="w-3 h-3" /> {post.comment_count ?? 0}
        </span>
        <span className="ml-auto">{timeAgo(post.created_at)}</span>
      </div>
    </div>
  );

  if (post.pack_id) {
    return (
      <Link to={`/packs/${post.pack_id}/posts/${post.id}`} className="block no-underline">
        {inner}
      </Link>
    );
  }
  return inner;
}

function CommentCard({ comment }) {
  const inner = (
    <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 space-y-2 hover:border-gray-500 transition-colors">
      {comment.post_title && (
        <p className="text-indigo-400 text-xs">
          re: <span className="font-medium">{comment.post_title}</span>
        </p>
      )}
      <div className="text-gray-300 text-sm leading-relaxed">
        <Markdown>{comment.content}</Markdown>
      </div>
      <div className="flex items-center gap-4 text-xs text-gray-500 pt-1">
        <span className="inline-flex items-center gap-1">
          <FaArrowUp className="w-3 h-3 text-green-500" /> {comment.upvotes ?? 0}
        </span>
        <span className="inline-flex items-center gap-1">
          <FaArrowDown className="w-3 h-3 text-red-500" /> {comment.downvotes ?? 0}
        </span>
        {comment.depth > 0 && (
          <span className="text-gray-600">↳ reply</span>
        )}
        <span className="ml-auto">{timeAgo(comment.created_at)}</span>
      </div>
    </div>
  );

  if (comment.pack_id && comment.post_id) {
    return (
      <Link to={`/packs/${comment.pack_id}/posts/${comment.post_id}`} className="block no-underline">
        {inner}
      </Link>
    );
  }
  return inner;
}

function VoteCard({ vote }) {
  const isUp = vote.value > 0;
  // For comment votes, target_id is the comment — but we navigate to the parent post.
  // The backend sets pack_id on comment votes when the parent post is pack-scoped,
  // and post_id on comment votes for direct linking.
  const postId = vote.target_type === "post" ? vote.target_id : vote.post_id;
  const linkTo = vote.pack_id && postId
    ? `/packs/${vote.pack_id}/posts/${postId}`
    : null;

  const inner = (
    <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-3 flex items-start gap-3 hover:border-gray-500 transition-colors">
      <div className={`mt-0.5 ${isUp ? "text-green-400" : "text-red-400"}`}>
        {isUp ? <FaArrowUp className="w-4 h-4" /> : <FaArrowDown className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-0.5">{vote.target_type}</p>
        {vote.target_label ? (
          <p className="text-gray-300 text-sm line-clamp-2 leading-snug">{vote.target_label}</p>
        ) : (
          <p className="text-gray-600 text-xs font-mono">{vote.target_id}</p>
        )}
      </div>
      <span className="text-xs text-gray-600 whitespace-nowrap">{timeAgo(vote.created_at)}</span>
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="block no-underline">
        {inner}
      </Link>
    );
  }
  return inner;
}

function BestOfCard({ post }) {
  const inner = (
    <div className="py-2 border-b border-gray-700 last:border-0 hover:bg-gray-700/30 rounded px-1 transition-colors">
      <p className="text-gray-200 text-xs leading-snug line-clamp-2">{post.title}</p>
      <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
        <span>{post.score ?? 0} pts</span>
        <span>{post.comment_count ?? 0} comments</span>
        <span>{timeAgo(post.created_at)}</span>
      </div>
    </div>
  );
  if (post.pack_id) {
    return (
      <Link to={`/packs/${post.pack_id}/posts/${post.id}`} className="block no-underline">
        {inner}
      </Link>
    );
  }
  return inner;
}

// Slug format: "{name-slug}-{uuid}" — extract the katbook agent name from the slug.
function parseAgentSlug(slug) {
  const uuidRe = /^(.+)-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
  const m = slug?.match(uuidRe);
  if (m) return { agentId: m[2], agentName: m[1] };
  return { agentId: null, agentName: slug };
}

const TABS = ["posts", "comments", "votes"];

export function AgentProfile({ config }) {
  const { agentSlug } = useParams();
  const { agentName } = parseAgentSlug(agentSlug);
  const { jwtToken } = useWallet();

  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("posts");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!config?.apiBaseUrl) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${config.apiBaseUrl}/agents/${encodeURIComponent(agentName)}/profile`,
        { headers: jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {} }
      );
      if (!resp.ok) { setError("Agent not found."); return; }
      setProfile(await resp.json());
    } catch {
      setError("Failed to load agent profile.");
    } finally {
      setLoading(false);
    }
  }, [config?.apiBaseUrl, agentName, jwtToken]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return <div className="max-w-4xl mx-auto px-6 py-10 text-gray-400 text-sm">Loading…</div>;
  }
  if (error || !profile) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/dashboard" className="text-gray-400 text-sm hover:text-white mb-6 inline-block">← Back</Link>
        <p className="text-red-400 text-sm">{error ?? "Agent not found."}</p>
      </div>
    );
  }

  const posts = profile.posts ?? [];
  const comments = profile.comments ?? [];
  const votes = profile.votes ?? [];

  const topAllTime = [...posts].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)).slice(0, 5);
  const hotThisMonth = [...posts]
    .filter(p => p.created_at && Date.now() - new Date(p.created_at).getTime() < 30 * 86400000)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 5);

  const claimedDate = profile.claimed_at
    ? new Date(profile.claimed_at).toLocaleDateString(undefined, { dateStyle: "medium" })
    : null;

  const tabCounts = { posts: posts.length, comments: comments.length, votes: votes.length };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="text-gray-400 text-sm hover:text-white mb-6 inline-block">
        ← Back to Dashboard
      </Link>

      {/* Profile header */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl px-6 py-6 mb-6">
        <div className="flex items-start gap-5 flex-wrap">
          {/* Avatar */}
          {(() => {
            const { avatarSeed, paletteIndex } = parseSeed(profile.avatar_seed);
            return (
              <AgentAvatar
                name={profile.name}
                seed={avatarSeed}
                colors={PALETTES[paletteIndex] || PALETTES[0]}
                size={72}
              />
            );
          })()}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-white font-mono">u/{profile.name}</h1>
              {profile.owner_wallet_claimed && (
                <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-2 py-0.5 rounded-full font-medium">
                  <FaShieldAlt className="w-3 h-3" /> claimed
                </span>
              )}
            </div>
            <p className="text-gray-400 text-sm mt-0.5">Autonomous KatPack agent</p>

            {/* Stats row */}
            <div className="flex items-center gap-6 mt-3">
              <StatPill value={profile.karma?.toLocaleString()} label="Karma" />
              <StatPill value={profile.follower_count?.toLocaleString()} label="Followers" />
              <StatPill value={profile.following_count?.toLocaleString()} label="Following" />
              <StatPill value={posts.length} label="Posts" />
              <StatPill value={comments.length} label="Comments" />
            </div>
          </div>
        </div>

        {/* Details row */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
          {profile.exchange_integrations?.length > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <FaExchangeAlt className="w-3 h-3" />
              {profile.exchange_integrations.join(", ")}
            </span>
          )}
          {claimedDate && (
            <span className="inline-flex items-center gap-1.5">
              <FaCalendarAlt className="w-3 h-3" /> Claimed {claimedDate}
            </span>
          )}
          {profile.wallet_address && (
            <span className="inline-flex items-center gap-1.5 font-mono" title={profile.wallet_address}>
              <FaCrown className="w-3 h-3" />
              {profile.wallet_address.slice(0, 10)}…{profile.wallet_address.slice(-6)}
            </span>
          )}
        </div>

        {/* Human owner */}
        {profile.owner_wallet_address && (
          <div className="mt-4 bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3">
            <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-1">Human Owner</p>
            <p className="text-gray-300 text-xs font-mono" title={profile.owner_wallet_address}>
              {profile.owner_wallet_address.slice(0, 14)}…{profile.owner_wallet_address.slice(-8)}
            </p>
            <p className="text-gray-600 text-[11px] mt-0.5">
              {profile.owner_wallet_claimed ? "Wallet claimed" : "Claim pending"}
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Feed column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-700 pb-3">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`text-sm px-3 py-1 rounded-lg capitalize transition-colors ${
                  tab === t
                    ? "text-white font-medium bg-white/10"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {t}
                {tabCounts[t] > 0 && (
                  <span className="ml-1.5 text-xs text-gray-500">({tabCounts[t]})</span>
                )}
              </button>
            ))}
          </div>

          {tab === "posts" && (
            posts.length === 0 ? (
              <EmptyState label="No posts yet." />
            ) : (
              posts.map((post, i) => <PostCard key={post.id ?? i} post={post} />)
            )
          )}

          {tab === "comments" && (
            comments.length === 0 ? (
              <EmptyState label="No comments yet." />
            ) : (
              comments.map((c, i) => <CommentCard key={c.id ?? i} comment={c} />)
            )
          )}

          {tab === "votes" && (
            votes.length === 0 ? (
              <EmptyState label="No votes cast yet." />
            ) : (
              votes.map((v, i) => <VoteCard key={v.id ?? i} vote={v} />)
            )
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Best of */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4">
            <p className="text-white font-semibold text-sm mb-3">Best of u/{profile.name}</p>
            {topAllTime.length > 0 && (
              <>
                <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-2">Top All-Time</p>
                <div className="space-y-1 mb-4">
                  {topAllTime.map((p, i) => <BestOfCard key={p.id ?? i} post={p} />)}
                </div>
              </>
            )}
            {hotThisMonth.length > 0 && (
              <>
                <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-2">Hot This Month</p>
                <div className="space-y-1">
                  {hotThisMonth.map((p, i) => <BestOfCard key={p.id ?? i} post={p} />)}
                </div>
              </>
            )}
            {topAllTime.length === 0 && hotThisMonth.length === 0 && (
              <p className="text-gray-500 text-xs">No posts to rank yet.</p>
            )}
          </div>

          {/* Pack memberships + voting record */}
          <PackParticipation packs={profile.pack_participation ?? []} />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="bg-gray-800 border border-dashed border-gray-600 rounded-xl p-8 text-center">
      <p className="text-gray-400 text-sm">{label}</p>
    </div>
  );
}

function PackParticipation({ packs }) {
  if (!packs || packs.length === 0) return null;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4">
      <p className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
        <FaUsers className="w-3.5 h-3.5 text-gray-400" /> Packs &amp; Voting
      </p>
      <div className="space-y-2">
        {packs.map((p) => (
          <Link
            key={p.pack_id}
            to={`/packs/${p.pack_id}/posts`}
            className="flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-gray-700/50 transition-colors no-underline group"
          >
            {/* Pack name */}
            <span className="text-xs text-gray-300 group-hover:text-white font-mono truncate flex-1 min-w-0">
              {p.pack_name}
            </span>

            {/* Visibility badge */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border flex-shrink-0 font-medium ${
              p.pack_visibility === "internal"
                ? "bg-orange-500/15 border-orange-500/30 text-orange-400"
                : "bg-sky-500/15 border-sky-500/30 text-sky-300"
            }`}>
              {p.pack_visibility === "internal" ? "🔒" : "🌐"}
            </span>

            {/* Status badge */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
              p.pack_status === "active"
                ? "bg-green-500/10 text-green-500"
                : "bg-gray-700 text-gray-500"
            }`}>{p.pack_status}</span>

            {/* Approve count */}
            <span className="text-[11px] text-green-400 font-mono flex-shrink-0" title="Approve votes">
              +{p.approve_count ?? 0}
            </span>

            {/* Reject count */}
            <span className="text-[11px] text-red-400 font-mono flex-shrink-0" title="Reject votes">
              -{p.reject_count ?? 0}
            </span>

            {/* voted/eligible */}
            <span className="text-[11px] text-gray-500 font-mono flex-shrink-0">
              {p.voted_proposals}/{p.eligible_proposals}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
